import './form.css';
import get from 'lodash/get';
import ArrowDown from '../../assets/arrow-down.svg';
import NumberInput from '@thesavingsgroup/components/NumberInput/NumberInput';
import Select from '@thesavingsgroup/components/Select/Select';
import Button from '@thesavingsgroup/components/Button/Button';
import type { Logger } from '@thesavingsgroup/logger/Logger';
import type { RemoteConfiguration } from '@thesavingsgroup/remote-configuration/types/RemoteConfiguration';
import { useTheme } from '@thesavingsgroup/components/theme/useTheme';
import { useQueryParams } from '../../hooks/useQueryParams';
import { useForm } from '../../hooks/useForm';
import { isValidLoanType } from '@thesavingsgroup/rate-table/isValidLoanType';
import { isValidCreditScoreRange } from '@thesavingsgroup/rate-table/isValidCreditScoreRange';
import { isValidLoanTerm } from '@thesavingsgroup/rate-table/isValidLoanTerm';
import { stripNonNumericCharacters } from '@thesavingsgroup/utils/stripNonNumericCharacters';
import {
  DEFAULT_CREDIT_SCORE_RANGE,
  DEFAULT_LOAN_TERM,
  DEFAULT_LOAN_TYPE,
} from '../../constants';
import type { Dispatch, StateUpdater } from 'preact/hooks';
import { useEffect } from 'preact/hooks';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useRateTable } from '../../hooks/useRateTable/useRateTable';

const Form = ({
  configuration,
  logger,
  setShowOffers,
  setSubmittedLoanType,
}: {
  configuration: RemoteConfiguration;
  logger: Logger;
  setShowOffers: Dispatch<StateUpdater<boolean>>;
  setSubmittedLoanType: Dispatch<StateUpdater<string>>;
}) => {
  const theme = useTheme();
  const queryParams = useQueryParams();
  const { loading, getRatesForBrands, version: ratesVersion } = useRateTable();
  const { sendSubmittedRateCalcOffer, sendUpdatedRateCalcOffer } =
    useAnalytics(logger);

  const creditTierParameter = get(queryParams, 'rateCalculator.creditTier');
  const loanAmountParameter = get(queryParams, 'rateCalculator.loanAmount');
  const loanTermParameter = get(queryParams, 'rateCalculator.loanTerm');
  const loanTypeParameter = get(queryParams, 'rateCalculator.loanType');
  const showResults = get(queryParams, 'rateCalculator.showResults', false);

  const submitButtonText = get(configuration, 'buttonCopy', 'Update offers');

  const initialCreditTier = isValidCreditScoreRange(creditTierParameter)
    ? creditTierParameter
    : DEFAULT_CREDIT_SCORE_RANGE;

  const initialLoanAmount = stripNonNumericCharacters(loanAmountParameter);

  const initialLoanTerm = isValidLoanTerm(loanTermParameter)
    ? loanTermParameter
    : DEFAULT_LOAN_TERM;

  const initialLoanType = isValidLoanType(loanTypeParameter)
    ? loanTypeParameter
    : DEFAULT_LOAN_TYPE;

  const { register, validateForm, formValues, errors } = useForm({
    initialValues: {
      loanAmount: initialLoanAmount,
      creditScore: initialCreditTier,
      loanTerm: initialLoanTerm,
      loanType: initialLoanType,
    },
    validationRules: {
      loanAmount: (value: string) => {
        const parsedValue = stripNonNumericCharacters(value);

        if (Number(parsedValue) >= 150000) {
          return 'Must be less than $150,000';
        }

        return '';
      },
      creditScore: (value: string) =>
        !value ? 'Credit score is required' : '',
      loanTerm: (value: string) => (!value ? 'Loan term is required' : ''),
      loanType: (value: string) => (!value ? 'Loan type is required' : ''),
    },
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();

    const formIsValid = validateForm();

    if (!formIsValid) {
      return;
    }

    setSubmittedLoanType(formValues.loanType);

    setShowOffers((prevShowOffers) => {
      getRatesForBrands({
        creditScoreRange: formValues.creditScore,
        loanAmount: formValues.loanAmount,
        loanTerm: formValues.loanTerm,
        loanType: formValues.loanType,
      }).finally(() => {
        if (prevShowOffers) {
          sendUpdatedRateCalcOffer({ ratesVersion });
        } else {
          sendSubmittedRateCalcOffer({ ratesVersion });
        }

        setShowOffers(true);
      });

      // do not show offers while loading
      return false;
    });
  };

  useEffect(() => {
    if (showResults) {
      onSubmit(new SubmitEvent('submit'));
    }
  }, [showResults]);

  return (
    <form onSubmit={onSubmit} noValidate>
      <fieldset>
        <NumberInput
          {...register('loanAmount')}
          aria-label="Loan amount"
          label="Loan amount"
          error={Boolean(errors.loanAmount)}
          currency
        />

        {errors.loanAmount && (
          <span style={{ color: theme.errorColor }}>{errors.loanAmount}</span>
        )}
      </fieldset>

      <fieldset>
        <Select
          {...register('creditScore')}
          aria-label="Credit score"
          label="Credit score"
          error={Boolean(errors.creditScore)}
          trailingIcon={ArrowDown}
          options={[
            { value: '800+', label: 'Excellent (800+)' },
            { value: '740-799', label: 'Great (740-799)' },
            { value: '670-739', label: 'Good (670-739)' },
            { value: '580-669', label: 'Fair (580-669)' },
            { value: '579 or lower', label: 'Poor (<579)' },
          ]}
        />

        {errors.creditScore && (
          <span style={{ color: theme.errorColor }}>{errors.creditScore}</span>
        )}
      </fieldset>

      <fieldset>
        <Select
          {...register('loanTerm')}
          aria-label="Loan term"
          label="Loan term"
          error={Boolean(errors.loanTerm)}
          trailingIcon={ArrowDown}
          options={[
            { value: '84', label: '84 months' },
            { value: '72', label: '72 months' },
            { value: '60', label: '60 months' },
            { value: '48', label: '48 months' },
            { value: '36', label: '36 months' },
            { value: '24', label: '24 months' },
          ]}
        />

        {errors.loanTerm && (
          <span style={{ color: theme.errorColor }}>{errors.loanTerm}</span>
        )}
      </fieldset>

      <fieldset>
        <Select
          {...register('loanType')}
          aria-label="Loan type"
          label="Loan type"
          error={Boolean(errors.loanType)}
          trailingIcon={ArrowDown}
          options={[
            { value: 'refinance', label: 'Refinance' },
            { value: 'purchase', label: 'Purchase' },
            { value: 'leaseBuyout', label: 'Lease Buyout' },
          ]}
        />

        {errors.loanType && (
          <span style={{ color: theme.errorColor }}>{errors.loanType}</span>
        )}
      </fieldset>

      <Button aria-label="Submit" type="submit" loader={loading} fullWidth>
        {submitButtonText}
      </Button>
    </form>
  );
};

export default Form;
